/**
 * StorageService - Manages localStorage operations for composition persistence
 * 
 * This service provides a type-safe interface for persisting composition data to
 * localStorage with comprehensive error handling. It handles serialization,
 * deserialization, validation, and error recovery for all storage operations.
 * 
 * ## Error Handling Strategy
 * 
 * All storage operations use a consistent error handling approach:
 * 
 * - **Quota Exceeded**: Detected via DOMException and thrown as StorageError with
 *   QUOTA_EXCEEDED type. Provides specific guidance for users to clear data.
 * 
 * - **Parse Failures**: Invalid JSON or corrupted data throws StorageError with
 *   PARSE_FAILED type. Consumers should fall back to default state.
 * 
 * - **Validation Failures**: Compositions that don't match the expected structure
 *   throw StorageError with PARSE_FAILED type.
 * 
 * - **Generic Failures**: Other localStorage errors throw StorageError with
 *   appropriate type (SAVE_FAILED, LOAD_FAILED, CLEAR_FAILED).
 * 
 * All errors include the original error object for debugging purposes.
 * 
 * ## Storage Format
 * 
 * Compositions are stored as JSON strings under the key 'compositionState'.
 * The format matches the Composition type with channels, notes, tempo, etc.
 * 
 * Ported from js/storage.js with TypeScript types and enhanced error handling.
 */

import type { Composition, InstrumentType } from '../../types/composition';
import { instrumentDatabase } from '../instruments/InstrumentDatabase';

/**
 * Storage keys used in localStorage
 */
export const STORAGE_KEYS = {
  COMPOSITION: 'compositionState',
  ANON_ID: 'anonId',
  ACCESS_TOKEN: 'accessToken',
  ID_TOKEN: 'idToken',
} as const;

/**
 * Error types for storage operations
 */
export enum StorageErrorType {
  SAVE_FAILED = 'SAVE_FAILED',
  LOAD_FAILED = 'LOAD_FAILED',
  PARSE_FAILED = 'PARSE_FAILED',
  CLEAR_FAILED = 'CLEAR_FAILED',
  QUOTA_EXCEEDED = 'QUOTA_EXCEEDED',
}

/**
 * Custom error class for storage operations
 */
export class StorageError extends Error {
  constructor(
    public type: StorageErrorType,
    message: string,
    public originalError?: unknown
  ) {
    super(message);
    this.name = 'StorageError';
  }
}

/**
 * StorageService class for managing localStorage operations
 * 
 * Provides methods for saving, loading, and managing composition data in localStorage.
 * All methods throw StorageError on failure for consistent error handling.
 */
export class StorageService {
  /**
   * Save composition to localStorage
   * 
   * Serializes the composition to JSON and stores it under the 'compositionState' key.
   * This operation is synchronous and typically completes in under 1ms for normal
   * composition sizes.
   * 
   * @param composition - The composition to save
   * @throws {StorageError} QUOTA_EXCEEDED if localStorage is full
   * @throws {StorageError} SAVE_FAILED for other save errors
   */
  saveComposition(composition: Composition): void {
    try {
      const serialized = JSON.stringify(composition);
      localStorage.setItem(STORAGE_KEYS.COMPOSITION, serialized);
    } catch (error) {
      // Check if quota exceeded
      if (error instanceof DOMException && error.name === 'QuotaExceededError') {
        throw new StorageError(
          StorageErrorType.QUOTA_EXCEEDED,
          'Storage quota exceeded. Please clear some data.',
          error
        );
      }
      
      throw new StorageError(
        StorageErrorType.SAVE_FAILED,
        'Failed to save composition to localStorage',
        error
      );
    }
  }

  /**
   * Load composition from localStorage
   * 
   * Retrieves and deserializes the composition from the 'compositionState' key.
   * Validates the structure before returning to ensure data integrity.
   * Applies migration logic for legacy compositions without instrument parameters.
   * 
   * @returns The loaded composition or null if no composition is stored
   * @throws {StorageError} PARSE_FAILED if JSON is invalid or structure is incorrect
   * @throws {StorageError} LOAD_FAILED for other load errors
   */
  loadComposition(): Composition | null {
    try {
      const compositionData = localStorage.getItem(STORAGE_KEYS.COMPOSITION);
      
      if (!compositionData) {
        return null;
      }

      const parsed = JSON.parse(compositionData);
      
      // Basic validation
      if (!this.isValidComposition(parsed)) {
        throw new StorageError(
          StorageErrorType.PARSE_FAILED,
          'Invalid composition format in localStorage'
        );
      }

      // Apply migration for legacy compositions
      const migratedComposition = this.migrateComposition(parsed);

      return migratedComposition as Composition;
    } catch (error) {
      if (error instanceof StorageError) {
        throw error;
      }

      if (error instanceof SyntaxError) {
        throw new StorageError(
          StorageErrorType.PARSE_FAILED,
          'Failed to parse composition data',
          error
        );
      }

      throw new StorageError(
        StorageErrorType.LOAD_FAILED,
        'Failed to load composition from localStorage',
        error
      );
    }
  }

  /**
   * Clear composition from localStorage
   * 
   * Removes the composition data from localStorage. This is typically called
   * when the user wants to start fresh or when recovering from corrupted data.
   * 
   * @throws {StorageError} CLEAR_FAILED if removal fails
   */
  clearComposition(): void {
    try {
      localStorage.removeItem(STORAGE_KEYS.COMPOSITION);
    } catch (error) {
      throw new StorageError(
        StorageErrorType.CLEAR_FAILED,
        'Failed to clear composition from localStorage',
        error
      );
    }
  }

  /**
   * Check if a composition exists in localStorage
   * 
   * @returns True if composition exists
   */
  hasComposition(): boolean {
    try {
      return localStorage.getItem(STORAGE_KEYS.COMPOSITION) !== null;
    } catch {
      return false;
    }
  }

  /**
   * Get storage size estimate in bytes
   * 
   * @returns Approximate size of stored composition in bytes
   */
  getStorageSize(): number {
    try {
      const data = localStorage.getItem(STORAGE_KEYS.COMPOSITION);
      return data ? new Blob([data]).size : 0;
    } catch {
      return 0;
    }
  }

  /**
   * Migrate legacy composition to include instrument parameters
   * 
   * This method applies default parameters to channels that don't have them,
   * ensuring backward compatibility with compositions created before the
   * enhanced instrument system was implemented.
   * 
   * @param composition - The composition to migrate
   * @returns Migrated composition with parameters
   */
  private migrateComposition(composition: Composition): Composition {
    return {
      ...composition,
      channels: composition.channels.map((channel) => {
        // If channel already has parameters, return as-is
        if (channel.parameters) {
          return channel;
        }

        // Apply default parameters for legacy instruments
        const instrumentMetadata = instrumentDatabase.getInstrument(
          channel.name as InstrumentType
        );

        if (instrumentMetadata) {
          return {
            ...channel,
            parameters: instrumentMetadata.defaultParameters,
          };
        }

        // If instrument not found in database, return channel without parameters
        // This handles edge cases where custom or unknown instruments might exist
        return channel;
      }),
    };
  }

  /**
   * Validate composition structure
   * 
   * @param data - Data to validate
   * @returns True if valid composition
   */
  private isValidComposition(data: unknown): data is Composition {
    if (!data || typeof data !== 'object') {
      return false;
    }

    const comp = data as Partial<Composition>;

    return (
      typeof comp.title === 'string' &&
      typeof comp.tempo === 'number' &&
      Array.isArray(comp.channels) &&
      comp.channels.every(
        (ch) =>
          typeof ch.name === 'string' &&
          typeof ch.patternLength === 'number' &&
          Array.isArray(ch.notes)
      )
    );
  }

  /**
   * Save generic data to localStorage
   * 
   * @param key - Storage key
   * @param value - Value to store
   */
  setItem(key: string, value: string): void {
    try {
      localStorage.setItem(key, value);
    } catch (error) {
      if (error instanceof DOMException && error.name === 'QuotaExceededError') {
        throw new StorageError(
          StorageErrorType.QUOTA_EXCEEDED,
          'Storage quota exceeded',
          error
        );
      }
      throw new StorageError(
        StorageErrorType.SAVE_FAILED,
        `Failed to save item: ${key}`,
        error
      );
    }
  }

  /**
   * Get generic data from localStorage
   * 
   * @param key - Storage key
   * @returns Stored value or null
   */
  getItem(key: string): string | null {
    try {
      return localStorage.getItem(key);
    } catch (error) {
      console.error(`Failed to get item: ${key}`, error);
      return null;
    }
  }

  /**
   * Remove generic data from localStorage
   * 
   * @param key - Storage key
   */
  removeItem(key: string): void {
    try {
      localStorage.removeItem(key);
    } catch (error) {
      console.error(`Failed to remove item: ${key}`, error);
    }
  }

  /**
   * Clear all localStorage data
   */
  clearAll(): void {
    try {
      localStorage.clear();
    } catch (error) {
      throw new StorageError(
        StorageErrorType.CLEAR_FAILED,
        'Failed to clear localStorage',
        error
      );
    }
  }
}

// Export singleton instance
export const storageService = new StorageService();
